import React, { useState, useEffect } from 'react';
import { Alert, Grid, Button, Checkbox } from '@nextiva/next-ui-components';

import { getAuthDomainInfo, authenticateDomain } from 'api/domainApi';
import Drawer from 'containers/Drawer';
import { AUTHENTICATE_DOMAIN } from 'constants/drawerTitles';
import { useSnackbar } from 'hooks';

const AuthenticateDomain = ({
  toggleDrawer,
  showAuthenticationDrawer,
  selectedDomain,
}) => {
  const [authDomainInfo, setAuthDomainInfo] = useState([]);
  const [isError, setIsError] = useState(null);
  const [checked, toggleCheckbox] = useState({ 0: false, 1: false, 2: false });
  const [isDisabled, setIsDisabled] = useState(false);

  const setSnackbar = useSnackbar(true);

  const resetCheckboxes = () => {
    toggleCheckbox({
      0: false,
      1: false,
      2: false,
    });
  };

  const handleAuthErrors = (networkResponse) => {
    if (networkResponse.dnsStatuses?.length > 0) {
      const dnsErrors = networkResponse.dnsStatuses.filter(
        (dnsStatus) => dnsStatus.valid === false,
      );

      // makes the DNS errors clearer to the user
      const formattedErrors = [
        'There was an error while authenticating your domain. Please review your DNS for the following errors:',
        <br />,
        <br />,
      ];

      dnsErrors.forEach((err) => {
        formattedErrors.push(err.reason);
        formattedErrors.push(<br />);
        formattedErrors.push(<br />);
      });
      formattedErrors.pop(); // get rid of the last <br /> to avoid too much empty space

      setIsError(formattedErrors);
    } else {
      // handle non-DNS errors
      setIsError(
        'There was an error while authenticating your domain. Please try again later or contact support if the issue persists.',
      );
    }
  };

  const submit = () => {
    authenticateDomain(selectedDomain)
      .then((response) => {
        if (response.authenticated) {
          setSnackbar({
            message:
              'Please wait 24-48 hours for the changes to apply, if your domain is not verified by then please verify your CNAME records.',
            severity: 'sucess',
          });
          resetCheckboxes();
          toggleDrawer();
        } else {
          handleAuthErrors(response);
        }
      })
      .catch(() => {
        setIsError(
          'There was an error while authenticating your domain. Please try again later or contact support if the issue persists.',
        );
      });
  };

  const handleCheckbox = (i) => (e) => {
    toggleCheckbox({
      ...checked,
      [i]: (checked[i] = e.target.checked),
    });
  };

  useEffect(() => {
    setIsError(null);
    getAuthDomainInfo(selectedDomain)
      .then((payload) => {
        setAuthDomainInfo(payload);
      })
      .catch(() => {
        setIsError('Unable to retrieve domain info.');
      });
  }, [selectedDomain]);

  useEffect(() => {
    setIsDisabled(!Object.values(checked).every((i) => !!i));
  }, [checked]);

  return (
    <Drawer
      title={AUTHENTICATE_DOMAIN}
      open={showAuthenticationDrawer}
      toggleDrawer={toggleDrawer}
    >
      <div className="m-authenticate-domain">
        <Grid container spacing={0}>
          <Grid className="alertContainer" item xs={12}>
            <Alert severity="info">
              These changes allow your outbound emails to appear to come from
              your domain instead of Nextiva. Follow the steps below.
              <br />
              <a
                href="https://www.nextiva.com/support/articles/setting-up-external-email-domains-for-nextos.html"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="PlatformAdminUI-AuthenticateDomain-Link"
              >
                I don&apos;t have access to DNS records
              </a>
            </Alert>
          </Grid>
          <Grid item xs={12}>
            <div className="header">Update your domain&apos;s DNS records</div>
          </Grid>
          <Grid item xs={12}>
            <div className="instructions">
              Log in to your DNS (domain name service) provider for{' '}
              <span className="instructions__domainText">{selectedDomain}</span>
              , navigate to your cpanel dashboard, and locate your email
              settings.
            </div>
          </Grid>
          {isError && (
            <Grid item xs={12}>
              <div className="errorContainer">
                <p className="errorContainer__errorMessage">{isError}</p>
              </div>
            </Grid>
          )}
          {authDomainInfo.map(({ host, data }, i) => (
            <Grid className="authenticationInstructions" container key={i}>
              <Grid
                item
                xs={1}
                className="authenticationInstructions__checkbox"
              >
                <Checkbox
                  height={20}
                  width={20}
                  checked={checked[i]}
                  onChange={handleCheckbox(i)}
                  data-testid="PlatformAdminUI-AuthenticateDomain-Checkbox"
                />
              </Grid>
              <Grid className="authenticationInstructions__step" item xs={11}>
                <div className="authenticationInstructions__stepTitle">
                  {i + 1}. Update {host}
                </div>
                <div className="authenticationInstructions__stepInstructions">
                  Paste in this new value:
                  <br />
                  <span>{data}</span>
                </div>
              </Grid>
            </Grid>
          ))}
          <Grid className="authenticationInfo" item xs={12}>
            <div className="authenticationInfo__authenticationInstructions">
              Once you&apos;ve made required DNS changes, return to this page
              and click Authenticate.{' '}
              <span className="authenticationInfo__authenticationWaitTimeText">
                Please wait 24-48 hours for the changes to apply.
              </span>
            </div>
          </Grid>
        </Grid>
        <div className="footer">
          <Grid container>
            <Grid className="footer__cancelButton" item xs={3}>
              <Button
                id="cancel"
                fullWidth
                color="secondary"
                variant="contained"
                onClick={toggleDrawer}
                data-testid="PlatformAdminUI-AuthenticateDomain-CancelButton"
              >
                Cancel
              </Button>
            </Grid>
            <Grid className="footer__authenticateButton" item xs={5}>
              <Button
                type="button"
                id="authenticateDomain"
                color="primary"
                variant="contained"
                fullWidth
                disabled={isDisabled}
                onClick={submit}
                data-testid="PlatformAdminUI-AuthenticateDomain-AuthenticateButton"
              >
                Authenticate
              </Button>
            </Grid>
          </Grid>
        </div>
      </div>
    </Drawer>
  );
};

export default AuthenticateDomain;
